/**
 * <gj-product> (handoff v2 §D): variant selection and gallery for sections/gj-main-product.liquid.
 * - Option radios → matching variant from the inline JSON → updates the hidden variant id read by Horizon's
 *   product-form-component, price / compare-at / Klarna installment, add-to-cart label and state, ?variant= in the
 *   URL, and scrolls the gallery to the variant photo.
 * - Values with no available variant (given the other selected options) are marked `is-unavailable`.
 * - Gallery (one photo at a time, horizontal scroll-snap): "1 / 4" counter and progress bars (mobile), prev/next arrows,
 *   ←/→ keys and the thumbnail strip (desktop) follow / drive the scroll; arrows are disabled at the ends.
 *
 * <gj-sticky-atc> (mobile only): sticky add-to-cart bar at the bottom of the product page.
 * - Shown as soon as the main buy row has scrolled above the screen (IntersectionObserver). Hidden only when it is
 *   in the way: while the footer covers more than 55% of the screen and while a drawer or dialog (minicart, menu,
 *   search, quick add) holds the scroll lock. Footers are tall (~800px on a phone) and product pages short: the old
 *   "footer in view" rule kept it hidden for the whole scroll.
 * - The cookie banner does not hide it: while the banner is up the bar sits just above it (--gj-sticky-bottom), and
 *   in theme previews above Shopify's preview bar (--gj-preview-bar from gj-components.js).
 * - Its button clicks the main add-to-cart button, so Horizon's product-form-component does the add (minicart, cart
 *   count, errors). When the selected options have no available variant it scrolls to the options and focuses them;
 *   when nothing is available it is disabled.
 * - Off screen it is `inert` (out of the tab order); with a drawer open it only slides away, so the drawer can give
 *   focus back to its button when it closes.
 *
 * <gj-dispatch> and <gj-product-tabs> (handoff D2 §3–4): see below.
 *
 * <gj-shipping-bar> (snippets/gj-shipping-bar.liquid with live): free shipping progress of the CURRENT cart against
 * data-threshold (cents), independent of the selected variant. Rendered by Liquid with the same numbers (no shift on
 * load); updated when the cart changes anywhere on the page (CartLinesUpdateEvent: product form, minicart, quick add).
 */
import { StandardEvents } from '@shopify/events';

const mobile = window.matchMedia('(max-width: 989px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class GjProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[data-gj-variants]');
    /** @type {Array<{ id: number, available: boolean, price: number, compare_at_price: number, options: string[], media_id: number }>} */
    this.variants = json ? JSON.parse(json.textContent || '[]') : [];
    this.gallery = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-gallery]'));

    this.addEventListener('change', this.#onChange);
    this.gallery?.addEventListener('scroll', this.#onGalleryScroll, { passive: true });
    this.#markAvailability();

    // Desktop, 2+ photos: arrows, thumbnails and ←/→ keys move one photo at a time (swipe/trackpad scroll natively)
    this.prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gj-gallery-prev]'));
    this.next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gj-gallery-next]'));
    this.thumbs = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-thumbs]'));
    this.prev?.addEventListener('click', this.#onPrev);
    this.next?.addEventListener('click', this.#onNext);
    this.thumbs?.addEventListener('click', this.#onThumb);
    this.gallery?.addEventListener('keydown', this.#onKey);

    // Start on the photo of the selected variant (e.g. ?variant= or first available)
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gj-variant-input]'));
    const current = (this.variants ?? []).find((v) => String(v.id) === input?.value);
    if (current?.media_id) this.#showMedia(current.media_id, false);

    this.info = /** @type {HTMLElement | null} */ (this.querySelector('.gj-pdp__info'));
    if (this.info) {
      this.infoObserver = new ResizeObserver(this.#placeInfo);
      this.infoObserver.observe(this.info);
      window.addEventListener('resize', this.#placeInfo, { passive: true });
    }
    this.gallery?.addEventListener('click', this.#onPhotoClick);
  }

  /** Desktop: a sticky info column taller than the screen sticks by its bottom edge (negative top), not its top. */
  #placeInfo = () => {
    if (!this.info) return;
    if (mobile.matches) return void this.info.style.removeProperty('--gj-info-top');
    const header = parseFloat(getComputedStyle(this.info).getPropertyValue('--gj-header-h')) || 0;
    const room = window.innerHeight - this.info.offsetHeight - 24;
    this.info.style.setProperty('--gj-info-top', `${Math.min(header, room)}px`);
  };

  /** Desktop: click on a photo opens it full screen. @param {Event} event */
  #onPhotoClick = (event) => {
    if (mobile.matches) return;
    const img = event.target instanceof Element ? event.target.closest('.gj-pdp__photo')?.querySelector('img') : null;
    if (!(img instanceof HTMLImageElement)) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'gj-lightbox';
    dialog.setAttribute('aria-label', img.alt);
    const big = document.createElement('img');
    big.className = 'gj-lightbox__img';
    big.alt = img.alt;
    big.srcset = img.srcset;
    big.sizes = '100vw';
    big.src = img.currentSrc || img.src;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'gj-lightbox__close';
    close.setAttribute('aria-label', this.dataset.textClose ?? 'Close');
    close.textContent = '×';
    dialog.append(close, big);
    dialog.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => dialog.remove());
    document.body.append(dialog);
    dialog.showModal();
  };

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.gallery?.removeEventListener('scroll', this.#onGalleryScroll);
    this.gallery?.removeEventListener('click', this.#onPhotoClick);
    this.infoObserver?.disconnect();
    window.removeEventListener('resize', this.#placeInfo);
    this.prev?.removeEventListener('click', this.#onPrev);
    this.next?.removeEventListener('click', this.#onNext);
    this.thumbs?.removeEventListener('click', this.#onThumb);
    this.gallery?.removeEventListener('keydown', this.#onKey);
  }

  get #selected() {
    return [...this.querySelectorAll('[data-gj-option]')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ?? ''
    );
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('[data-gj-option]')) return;

    const fieldset = input.closest('[data-gj-option]');
    const label = fieldset?.querySelector('[data-gj-option-value]');
    if (label) label.textContent = input.value;

    const selected = this.#selected;
    const variant = (this.variants ?? []).find((v) => v.options.every((value, i) => value === selected[i]));
    this.#markAvailability();
    this.#update(variant);
  };

  /**
   * Mark values that have no available variant with the other options as currently selected. In the quick add panel
   * with a single option (e.g. Finitura) sold out values are also disabled: the choice stays visible but can't be
   * picked. With several options they stay selectable, so a combination never locks the others.
   */
  #markAvailability() {
    const selected = this.#selected;
    const fieldsets = this.querySelectorAll('[data-gj-option]');
    const lock = this.hasAttribute('data-gj-quick') && fieldsets.length === 1;
    fieldsets.forEach((fieldset, index) => {
      for (const input of fieldset.querySelectorAll('input')) {
        const candidate = [...selected];
        candidate[index] = /** @type {HTMLInputElement} */ (input).value;
        const available = (this.variants ?? []).some(
          (v) => v.available && v.options.every((value, i) => value === candidate[i])
        );
        input.closest('.gj-value')?.classList.toggle('is-unavailable', !available);
        if (lock) /** @type {HTMLInputElement} */ (input).disabled = !available;
      }
    });
  }

  /** Shop money format (window.Giordi.formatMoney from gj-components.js). @param {number} cents */
  #money(cents) {
    const giordi = /** @type {any} */ (window).Giordi;
    return giordi?.formatMoney ? giordi.formatMoney(cents) : (cents / 100).toFixed(2);
  }

  /** @param {{ id: number, available: boolean, price: number, compare_at_price: number, media_id: number } | undefined} variant */
  #update(variant) {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gj-variant-input]'));
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('.gj-pdp__atc'));
    const label = this.querySelector('[data-gj-atc-label]');
    const texts = this.dataset;

    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = texts.textUnavailable ?? '';
      return;
    }

    if (input) {
      input.value = String(variant.id);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (button) button.disabled = !variant.available;
    if (label) label.textContent = (variant.available ? texts.textAdd : texts.textSoldOut) ?? '';

    for (const price of this.querySelectorAll('[data-gj-price]')) price.textContent = this.#money(variant.price);
    for (const compare of this.querySelectorAll('[data-gj-compare]')) {
      const onSale = variant.compare_at_price > variant.price;
      compare.toggleAttribute('hidden', !onSale);
      if (onSale) compare.textContent = this.#money(variant.compare_at_price);
    }
    const klarna = this.querySelector('[data-gj-klarna] [data-gj-installment]');
    if (klarna) klarna.textContent = this.#money(Math.floor(variant.price / 3));

    // The quick add panel (data-gj-quick) lives on other pages: their URL stays as it is
    if (!this.hasAttribute('data-gj-quick')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState(history.state, '', url);
    }

    if (variant.media_id) this.#showMedia(variant.media_id);
    this.querySelector('gj-sticky-atc')?.dispatchEvent(new CustomEvent('gj:variant', { detail: variant }));
  }

  /** @param {number} mediaId @param {boolean} [smooth] */
  #showMedia(mediaId, smooth = true) {
    const photo = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!(photo instanceof HTMLElement) || !this.gallery) return;
    this.#go([...this.gallery.children].indexOf(photo), smooth);
  }

  get #index() {
    const gallery = this.gallery;
    return gallery ? Math.round(gallery.scrollLeft / Math.max(1, gallery.clientWidth)) : 0;
  }

  /** Scroll the gallery to photo `index` (each photo is as wide as the gallery). @param {number} index @param {boolean} [smooth] */
  #go(index, smooth = true) {
    const gallery = this.gallery;
    if (!gallery || index < 0 || index >= gallery.children.length) return;
    const behavior = smooth && !reducedMotion.matches ? 'smooth' : 'auto';
    if (!mobile.matches) {
      // Desktop: photos are stacked, bring the one for this variant into view (nothing to do for the first)
      const photo = /** @type {HTMLElement} */ (gallery.children[index]);
      if (index > 0 && smooth) window.scrollTo({ top: window.scrollY + photo.getBoundingClientRect().top - 120, behavior });
      return;
    }
    gallery.scrollTo({ left: index * gallery.clientWidth, behavior });
  }

  #onPrev = () => this.#go(this.#index - 1);
  #onNext = () => this.#go(this.#index + 1);

  /** @param {Event} event */
  #onThumb = (event) => {
    const thumb = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-gj-thumb]'));
    if (thumb) this.#go(Number(thumb.dataset.gjThumb));
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#go(this.#index + (event.key === 'ArrowLeft' ? -1 : 1));
  };

  #frame = 0;
  #onGalleryScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const gallery = this.gallery;
      if (!gallery) return;
      const total = gallery.children.length;
      const index = Math.min(this.#index, total - 1);
      const counter = this.querySelector('[data-gj-gallery-counter]');
      if (counter) counter.textContent = `${index + 1} / ${total}`;
      this.querySelectorAll('.gj-pdp__bars span').forEach((bar, i) => bar.classList.toggle('is-active', i === index));
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === total - 1;
      const strip = this.thumbs;
      if (!strip) return;
      strip.querySelectorAll('[data-gj-thumb]').forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
          // Keep the active thumbnail in view inside the strip (never scrolls the page)
          const el = /** @type {HTMLElement} */ (thumb);
          if (el.offsetTop < strip.scrollTop || el.offsetTop + el.offsetHeight > strip.scrollTop + strip.clientHeight) {
            strip.scrollTo({ top: el.offsetTop - strip.clientHeight / 2 + el.offsetHeight / 2 });
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    });
  };
}

if (!customElements.get('gj-product')) customElements.define('gj-product', GjProduct);

class GjStickyAtc extends HTMLElement {
  #pastBuy = false;
  #footerCovers = false;
  #overlay = false;
  /** @type {IntersectionObserver[]} */ #ios = [];
  /** @type {MutationObserver | undefined} */ #mo;
  /** @type {ResizeObserver | undefined} */ #ro;

  connectedCallback() {
    this.product = /** @type {(HTMLElement & { variants?: Array<{ available: boolean }> }) | null} */ (
      this.closest('gj-product')
    );
    this.main = /** @type {HTMLButtonElement | null} */ (this.product?.querySelector('.gj-pdp__atc') ?? null);
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gj-sticky-button]'));
    const buy = this.product?.querySelector('.gj-pdp__buy');
    if (!this.main || !this.button || !buy) return;

    this.button.addEventListener('click', this.#onClick);
    this.addEventListener('gj:variant', this.#onVariant);

    // Past the buy row: it has left the screen at the top (not still below it)
    const buyIo = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#pastBuy = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#render();
    });
    buyIo.observe(buy);
    this.#ios.push(buyIo);

    // Footer covering more than 55% of the screen: it "intersects" a root shrunk to the top 45%
    const footer = document.querySelector('.gj-footer, footer');
    if (footer) {
      const footerIo = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.#footerCovers = entry.isIntersecting;
          this.#render();
        },
        { rootMargin: '0px 0px -55% 0px' }
      );
      footerIo.observe(footer);
      this.#ios.push(footerIo);
    }

    // Main button state (variant change, sold out), drawers/dialogs (scroll lock), cookie banner shown or closed
    this.#mo = new MutationObserver(() => this.#sync());
    this.#mo.observe(this.main, { attributes: true, attributeFilter: ['disabled'] });
    this.#mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'scroll-lock'] });
    this.cookie = document.querySelector('gj-cookie-banner');
    if (this.cookie) {
      this.#mo.observe(this.cookie, { attributes: true, attributeFilter: ['hidden'] });
      this.#ro = new ResizeObserver(this.#place);
      this.#ro.observe(this.cookie);
    }
    window.addEventListener('resize', this.#place, { passive: true });
    mobile.addEventListener('change', this.#render);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    this.removeEventListener('gj:variant', this.#onVariant);
    for (const io of this.#ios) io.disconnect();
    this.#ios = [];
    this.#mo?.disconnect();
    this.#ro?.disconnect();
    window.removeEventListener('resize', this.#place);
    mobile.removeEventListener('change', this.#render);
  }

  /** While the cookie banner is up, sit just above it instead of under it (the banner has the higher z-index). */
  #place = () => {
    const cookie = this.cookie;
    let lift = 0;
    if (cookie && !cookie.hidden && cookie.offsetHeight > 0) {
      // Layout values, not getBoundingClientRect: the banner slides in with a transform when it appears
      const bottom = parseFloat(getComputedStyle(cookie).bottom) || 0;
      lift = Math.round(bottom + cookie.offsetHeight + 8);
    }
    this.style.setProperty('--gj-sticky-bottom', `${lift}px`);
  };

  get #anyAvailable() {
    return (this.product?.variants ?? []).some((variant) => variant.available);
  }

  get #hasOptions() {
    return Boolean(this.product?.querySelector('[data-gj-option]'));
  }

  /** Label and state from the main button; overlays and cookie banner position from the page. */
  #sync() {
    const { main, button } = this;
    if (!main || !button) return;
    const texts = /** @type {HTMLElement} */ (this.product).dataset;
    const choose = main.disabled && this.#hasOptions && this.#anyAvailable;
    button.disabled = main.disabled && !choose;
    button.textContent = (main.disabled ? (choose ? this.dataset.textChoose : texts.textSoldOut) : texts.textAdd) ?? '';
    const root = document.documentElement;
    this.#overlay = root.classList.contains('gj-scroll-lock') || root.hasAttribute('scroll-lock');
    this.#place();
    this.#render();
  }

  #render = () => {
    const visible = mobile.matches && this.#pastBuy && !this.#footerCovers && !this.#overlay;
    this.classList.toggle('is-visible', visible);
    // With a drawer open the page is inert anyway: stay focusable so the drawer can restore focus to the button
    this.toggleAttribute('inert', !visible && !this.#overlay);
  };

  /** Follow the variant photo in the thumbnail. @param {Event} event */
  #onVariant = (event) => {
    const variant = /** @type {CustomEvent<{ media_id: number }>} */ (event).detail;
    const photo = variant?.media_id ? this.product?.querySelector(`[data-media-id="${variant.media_id}"] img`) : null;
    const thumb = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-gj-sticky-image]'));
    if (photo instanceof HTMLImageElement && thumb && photo.currentSrc) {
      thumb.srcset = '';
      thumb.src = photo.currentSrc;
    }
  };

  #onClick = () => {
    const main = this.main;
    if (!main) return;
    if (!main.disabled) {
      main.click();
      return;
    }
    // No available variant for the selected options: take the customer to the options
    const fieldset = this.product?.querySelector('[data-gj-option]');
    if (!(fieldset instanceof HTMLElement)) return;
    fieldset.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
    const input = /** @type {HTMLInputElement | null} */ (
      fieldset.querySelector('input:checked') ?? fieldset.querySelector('input')
    );
    input?.focus({ preventScroll: true });
  };
}

if (!customElements.get('gj-sticky-atc')) customElements.define('gj-sticky-atc', GjStickyAtc);

/**
 * <gj-dispatch> (handoff D2 §3): picks the "Spedito oggi / domani / lunedì" title from the shop's cut-off.
 * data-cutoff "HH:MM" and data-days (ISO weekdays, 1 = Monday) come from the theme settings; the time is read in
 * Europe/Rome whatever the visitor's clock. The three titles are stacked in one grid cell, so switching never moves
 * the layout. Re-checked every minute and when the tab comes back.
 */
class GjDispatch extends HTMLElement {
  /** @type {number | undefined} */ #timer;

  connectedCallback() {
    this.#update();
    this.#timer = window.setInterval(this.#update, 60_000);
    document.addEventListener('visibilitychange', this.#update);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.#update);
  }

  #update = () => {
    const [hours = NaN, minutes = 0] = (this.dataset.cutoff ?? '').split(/[:.]/).map(Number);
    const days = (this.dataset.days ?? '').split(',').map(Number).filter(Boolean);
    if (Number.isNaN(hours) || days.length === 0) return;

    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Rome',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date());
    } catch {
      return;
    }
    const part = (/** @type {string} */ type) => parts.find((p) => p.type === type)?.value ?? '';
    const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(part('weekday')) + 1;
    const now = Number(part('hour')) * 60 + Number(part('minute'));
    if (!weekday) return;

    let when = 'today';
    if (!days.includes(weekday) || now >= hours * 60 + minutes) {
      const tomorrow = (weekday % 7) + 1;
      when = days.includes(tomorrow) ? 'tomorrow' : 'monday';
    }
    for (const title of this.querySelectorAll('[data-when]')) {
      title.classList.toggle('is-active', /** @type {HTMLElement} */ (title).dataset.when === when);
    }
  };
}

if (!customElements.get('gj-dispatch')) customElements.define('gj-dispatch', GjDispatch);

/**
 * <gj-product-tabs> (handoff D2 §4): Descrizione / Info tabs and the 4-line description clamp.
 * - Tabs: click or ←/→/Home/End (roving tabindex), aria-selected, panels toggled with `hidden`.
 * - "Continua a leggere": shown only when the body is taller than the clamp (measured, re-measured on resize);
 *   opens by animating max-height to the content height (.45s), then drops the limit; "Mostra meno" goes back.
 *   Focus moving into the clipped text (a link) opens it.
 */
class GjProductTabs extends HTMLElement {
  /** @type {ResizeObserver | undefined} */ #ro;

  connectedCallback() {
    this.tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
    this.body = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-clamp]'));
    this.more = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-gj-more]'));

    for (const tab of this.tabs) {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKey);
    }
    this.more?.addEventListener('click', this.#onMore);
    this.body?.addEventListener('focusin', this.#onFocusIn);
    if (this.body) {
      this.#ro = new ResizeObserver(() => this.#measure());
      this.#ro.observe(this.body);
    }
  }

  disconnectedCallback() {
    for (const tab of this.tabs ?? []) {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKey);
    }
    this.more?.removeEventListener('click', this.#onMore);
    this.body?.removeEventListener('focusin', this.#onFocusIn);
    this.#ro?.disconnect();
  }

  /** @param {HTMLElement} tab @param {boolean} [focus] */
  #select(tab, focus = false) {
    for (const item of this.tabs ?? []) {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${item.getAttribute('aria-controls')}`);
      panel?.toggleAttribute('hidden', !selected);
    }
    if (focus) tab.focus();
    this.#measure();
  }

  /** @param {Event} event */
  #onTabClick = (event) => this.#select(/** @type {HTMLElement} */ (event.currentTarget));

  /** @param {KeyboardEvent} event */
  #onTabKey = (event) => {
    const tabs = this.tabs ?? [];
    const index = tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    const last = tabs.length - 1;
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    if (target) this.#select(target, true);
  };

  get #open() {
    return this.more?.getAttribute('aria-expanded') === 'true';
  }

  /** Show the button only when the text overflows the clamp (or is open). */
  #measure() {
    const { body, more } = this;
    if (!body || !more || body.offsetParent === null || this.#open) return;
    more.hidden = body.scrollHeight <= body.clientHeight + 2;
  }

  #onMore = () => this.#toggle(!this.#open);

  /** @param {boolean} open */
  #toggle(open) {
    const { body, more } = this;
    if (!body || !more) return;
    const label = more.querySelector('[data-gj-more-label]');
    more.setAttribute('aria-expanded', String(open));
    if (label) label.textContent = (open ? more.dataset.textLess : more.dataset.textMore) ?? '';

    const instant = reducedMotion.matches;
    if (open) {
      body.style.maxHeight = `${body.scrollHeight}px`;
      const done = () => {
        body.classList.add('is-open');
        body.style.maxHeight = '';
      };
      if (instant) done();
      else {
        body.addEventListener('transitionend', done, { once: true });
        window.setTimeout(done, 600); // no transitionend when the height does not change
      }
    } else {
      // From "none" back to the clamp: pin the current height first so the transition has a start value
      body.style.maxHeight = `${body.scrollHeight}px`;
      body.classList.remove('is-open');
      void body.offsetHeight;
      body.style.maxHeight = '';
    }
  }

  #onFocusIn = () => {
    if (!this.#open && this.more && !this.more.hidden) this.#toggle(true);
  };
}

if (!customElements.get('gj-product-tabs')) customElements.define('gj-product-tabs', GjProductTabs);

class GjShippingBar extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {Event} event */
  #onCart = (event) => {
    Promise.resolve(/** @type {any} */ (event).promise)
      .then(() => fetch(`${/** @type {any} */ (window).Theme?.routes?.cart_url ?? '/cart'}.js`))
      .then((response) => response.json())
      .then((cart) => {
        if (typeof cart?.total_price !== 'number') return;
        this.dataset.cartTotal = String(cart.total_price);
        this.#render();
      })
      .catch(() => {});
  };

  #render() {
    const threshold = Number(this.dataset.threshold) || 0;
    if (!threshold) return;
    const total = Number(this.dataset.cartTotal) || 0;
    const remaining = threshold - total;
    const percent = remaining > 0 ? Math.floor((total * 100) / threshold) : 100;

    const giordi = /** @type {any} */ (window).Giordi;
    const money = (/** @type {number} */ cents) => (giordi?.formatMoney ? giordi.formatMoney(cents) : (cents / 100).toFixed(2));
    const text = this.querySelector('[data-gj-ship-text]');
    let html = this.dataset.textFree ?? '';
    if (total <= 0) html = this.dataset.textEmpty ?? '';
    else if (remaining > 0) html = (this.dataset.textMissing ?? '').replace('[amount]', money(remaining));
    // Texts come from the theme locales (rendered by Liquid), not from the customer
    if (text && text.innerHTML !== html) text.innerHTML = html;

    const track = this.querySelector('[role="progressbar"]');
    track?.setAttribute('aria-valuenow', String(percent));
    const fill = /** @type {HTMLElement | null} */ (track?.querySelector('span') ?? null);
    if (fill) fill.style.width = `${percent}%`;
  }
}

if (!customElements.get('gj-shipping-bar')) customElements.define('gj-shipping-bar', GjShippingBar);

/**
 * <gj-engraving> (snippets in sections/gj-main-product.liquid): text field of the personalizable products, sent to the
 * cart as the line item property "Incisione". Shows the character count and, when required, blocks the add to cart
 * (main button and sticky bar, both submit this form) until the field has text.
 */
class GjEngraving extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input'));
    this.count = this.querySelector('[data-gj-engraving-count]');
    this.error = /** @type {HTMLElement | null} */ (this.querySelector('.gj-engraving__error'));
    this.warning = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-engraving-warning]'));
    this.form = this.closest('form');
    this.input?.addEventListener('input', this.#onInput);
    // Capture phase on window: Horizon's component.js dispatches `on:submit` from a document capture listener, this must run before it
    window.addEventListener('submit', this.#onSubmit, true);
    this.#onInput();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    window.removeEventListener('submit', this.#onSubmit, true);
  }

  #onInput = () => {
    if (!this.input) return;
    if (this.count) this.count.textContent = String(this.input.value.length);
    if (this.warning) this.warning.hidden = !this.input.value.trim();
    this.form?.querySelector('[data-gj-accel]')?.toggleAttribute('hidden', Boolean(this.input.value.trim()));
    if (this.input.value.trim()) this.#showError('');
  };

  /** @param {string} message */
  #showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
    this.input?.toggleAttribute('aria-invalid', Boolean(message));
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (event.target !== this.form || !this.input || this.input.value.trim()) return;
    if (this.dataset.required !== 'true') {
      // Optional engraving left empty: do not send an empty "Incisione" property with the line
      const input = this.input;
      input.disabled = true;
      window.setTimeout(() => (input.disabled = false), 600);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError(this.dataset.textRequired ?? '');
    this.input.focus();
  };
}

if (!customElements.get('gj-engraving')) customElements.define('gj-engraving', GjEngraving);
